import { useState } from '@hookstate/core';
import { useContext } from 'react';

import { Icon as BlueIcon } from '@blueprintjs/core';

import ReactDom from 'react-dom';

import { useMutation, useQueryClient } from 'react-query';

import { PulseLoader } from 'react-spinners';

import { formatDate } from '@utils/index';
import { isDefined } from '@utils/isNonNullable';

import { createTask, patchTask } from '@services/queries/tasks';
import { ITaskRawQuery } from '@models/Task.model';
import { Details } from './components/Details/Details';

import { CreateUpdateContext } from './CreateTaskDialog.query';
import { IDetailsFormData } from './components/Details/types';
import {
  Body,
  ButtonsContainer,
  Close,
  Confirm,
  Container,
  Divider,
  Header,
  Line,
  LoadingFilter,
  Overlay,
  Title,
} from './CreateTaskDialog.styles';

interface ICreateTaskDialogRaw {
  open: boolean;
  onClose: () => void;
  task?: ITaskRawQuery;
}

export const CreateTaskDialogRaw = ({
  open,
  onClose,
  task,
}: ICreateTaskDialogRaw) => {
  const portalDiv = document.getElementById('portal');

  const { detailsIsValid, title, date, priority, description } =
    useContext(CreateUpdateContext);

  const loadingCreate = useState<boolean>(false);

  const queryClient = useQueryClient();

  const createTaskMutation = useMutation(createTask, {
    onSuccess: async () => {
      onClose();

      detailsIsValid.set(false);
      title.set('');
      date.set(null);
      priority.set(null);
      description.set('');

      queryClient.invalidateQueries(['tasks']);
    },
  });

  const patchTaskMutation = useMutation(patchTask, {
    onSuccess: async () => {
      onClose();

      detailsIsValid.set(false);
      title.set('');
      date.set(null);
      priority.set(null);
      description.set('');

      queryClient.invalidateQueries(['tasks']);
    },
  });

  const NextButton = () => {
    if (task) {
      patchTaskMutation.mutate({
        taskId: task.id,
        title: title.value,
        description: description.value,
        dueDate: date.value ? new Date(date.value as string) : null,
        priority: priority.value,
      });
    } else {
      createTaskMutation.mutate({
        title: title.value,
        description: description.value,
        dueDate: date.value ? new Date(date.value as string) : undefined,
        priority: priority.value,
      });
    }
  };

  function syncUpdateDetails(newValues: Partial<IDetailsFormData>): void {
    if (isDefined(newValues.date)) {
      date.set(formatDate(newValues.date));
    }
    if (isDefined(newValues.priority)) {
      priority.set(newValues.priority);
    }
    if (isDefined(newValues.description)) {
      description.set(newValues.description);
    }
    if (isDefined(newValues.title)) title.set(newValues.title);
  }

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            {loadingCreate.value && (
              <LoadingFilter>
                <PulseLoader color="#1371fe" />
              </LoadingFilter>
            )}
            <Header>
              <Title>{task ? 'Edit Task' : 'Create Task'}</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider />

            <Body show>
              <Details
                task={task}
                mode={task ? 'editing' : 'create'}
                broadcast={syncUpdateDetails}
                onValidChange={detailsIsValid.set}
              />
            </Body>

            <Line>
              <ButtonsContainer>
                <Confirm
                  type="button"
                  onClick={NextButton}
                  disabled={!detailsIsValid.get()}
                >
                  {task ? 'Save Changes' : 'Create Task'}
                </Confirm>
              </ButtonsContainer>
            </Line>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
